import { Injectable } from '@angular/core'
import { AudioChunk } from './audio-capture-strategy.interface'

/**
 * AudioFormatAdapterService
 *
 * **目的**: 音声データフォーマットの変換を提供する
 *
 * **主要責務**:
 * - Base64 ↔ ArrayBuffer 双方向変換
 * - Blob → Base64/ArrayBuffer 変換
 * - AudioChunk型での統一的な音声データ正規化
 *
 * **依存関係**:
 * - なし (純粋な変換ユーティリティ)
 *
 * **使用例**:
 * ```typescript
 * const adapter = inject(AudioFormatAdapterService);
 *
 * // Base64をArrayBufferに変換
 * const buffer = adapter.base64ToArrayBuffer(base64String);
 *
 * // AudioChunkをBase64形式に正規化
 * const normalized = adapter.normalizeToBase64(audioChunk);
 * ```
 *
 * **要件**: 2.5 - 音声出力フォーマット正規化
 */
@Injectable({
  providedIn: 'root',
})
export class AudioFormatAdapterService {
  /**
   * Base64文字列をArrayBufferに変換
   *
   * @param base64 Base64エンコードされた文字列
   * @returns ArrayBuffer形式の音声データ
   * @throws Error base64が無効な場合
   */
  base64ToArrayBuffer(base64: string): ArrayBuffer {
    if (base64 == null) {
      throw new Error('base64 string cannot be null or undefined')
    }

    try {
      const binaryString = atob(base64)
      const len = binaryString.length
      const bytes = new Uint8Array(len)
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return bytes.buffer
    } catch (error) {
      throw new Error(`Invalid base64 string: ${error}`)
    }
  }

  /**
   * ArrayBufferをBase64文字列に変換
   *
   * @param buffer ArrayBuffer形式の音声データ
   * @returns Base64エンコードされた文字列
   * @throws Error bufferが無効な場合
   */
  arrayBufferToBase64(buffer: ArrayBuffer): string {
    if (buffer == null) {
      throw new Error('ArrayBuffer cannot be null or undefined')
    }

    const bytes = new Uint8Array(buffer)
    let binary = ''
    const len = bytes.byteLength
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
  }

  /**
   * BlobをBase64文字列に変換
   *
   * @param blob Blob形式の音声データ
   * @returns Base64エンコードされた文字列のPromise
   */
  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // data:audio/webm;base64,... の形式から base64 部分のみを抽出
          const base64 = reader.result.split(',')[1] || ''
          resolve(base64)
        } else {
          reject(new Error('FileReader result is not a string'))
        }
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
  }

  /**
   * BlobをArrayBufferに変換
   *
   * @param blob Blob形式の音声データ
   * @returns ArrayBuffer形式の音声データのPromise
   */
  async blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result)
        } else {
          reject(new Error('FileReader result is not an ArrayBuffer'))
        }
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsArrayBuffer(blob)
    })
  }

  /**
   * AudioChunkのデータをBase64文字列形式に正規化
   *
   * @param chunk 任意のフォーマットのAudioChunk
   * @returns データがBase64文字列形式に正規化されたAudioChunk
   */
  normalizeToBase64(chunk: AudioChunk): AudioChunk {
    if (typeof chunk.data === 'string') {
      // すでに文字列(Base64)形式の場合はそのまま返す
      return chunk
    } else if (chunk.data instanceof ArrayBuffer) {
      // ArrayBufferをBase64に変換
      const base64 = this.arrayBufferToBase64(chunk.data)
      return {
        format: chunk.format,
        data: base64,
        sequence: chunk.sequence,
        timestamp: chunk.timestamp,
        ...(chunk.isComplete != null && { isComplete: chunk.isComplete }),
      }
    } else {
      throw new Error(`Unsupported audio data type: ${typeof chunk.data}`)
    }
  }

  /**
   * AudioChunkのデータをArrayBuffer形式に正規化
   *
   * @param chunk 任意のフォーマットのAudioChunk
   * @returns データがArrayBuffer形式に正規化されたAudioChunk
   */
  normalizeToArrayBuffer(chunk: AudioChunk): AudioChunk {
    if (chunk.data instanceof ArrayBuffer) {
      // すでにArrayBuffer形式の場合はそのまま返す
      return chunk
    } else if (typeof chunk.data === 'string') {
      // Base64をArrayBufferに変換
      const buffer = this.base64ToArrayBuffer(chunk.data)
      return {
        format: chunk.format,
        data: buffer,
        sequence: chunk.sequence,
        timestamp: chunk.timestamp,
        ...(chunk.isComplete != null && { isComplete: chunk.isComplete }),
      }
    } else {
      throw new Error(`Unsupported audio data type: ${typeof chunk.data}`)
    }
  }
}
